import { useEffect } from 'react'
import { isFleetVoiceOrg } from 'lib/utils/auth'
import { useFleetAdapter } from './adapters/useFleetAdapter'
import { useZelloAdapter } from './adapters/useZelloAdapter'
import type { VoiceBackendKind, VoiceClient, VoiceClientContext } from './types'

type UseVoiceClientArgs = Omit<VoiceClientContext, 'enabled'> & {
  refetchCurrentUser: () => Promise<unknown>
}

export function voiceBackendFor(org: VoiceClientContext['org']): VoiceBackendKind {
  return isFleetVoiceOrg(org) ? 'fleet' : 'zello'
}

// Picks the adapter for the selected org's backend. Hooks can't be called
// conditionally, so every adapter is mounted and all but the chosen one is
// kept idle; switching backend disconnects the one left behind.
export function useVoiceClient({ refetchCurrentUser, ...context }: UseVoiceClientArgs): VoiceClient {
  const backend = voiceBackendFor(context.org)

  const zello = useZelloAdapter({ ...context, enabled: backend === 'zello' })
  const fleet = useFleetAdapter({ ...context, enabled: backend === 'fleet' }, refetchCurrentUser)

  const active = backend === 'fleet' ? fleet : zello
  const idle = backend === 'fleet' ? zello : fleet

  useEffect(() => {
    void idle.disconnectAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend])

  return active
}
